import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

// Fallback updated to 3001 to match the actual dev server port.
// Always set REACT_APP_API_URL in your .env to avoid relying on this fallback.
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — attach JWT token from localStorage
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('access_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — global error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    // FIX: Network / connection errors (no response at all).
    // Shape the rejection so every catch block can uniformly read
    // err.response.status and err.response.data.message.
    // The `isNetworkError` flag lets components distinguish this from API errors.
    if (!error.response) {
      console.error('Network Error: Backend server not reachable at', BASE_URL);
      return Promise.reject({
        isNetworkError: true,
        response: {
          status: 0,
          data: {
            error: 'Connection Error',
            message: `Cannot connect to backend server at ${BASE_URL}. Ensure the server is running and REACT_APP_API_URL is correct.`,
          },
        },
      });
    }

    // 401 — attempt silent token refresh, then re-try the original request once
    if (error.response.status === 401) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken && !(error.config as any)?._retry) {
        (error.config as any)._retry = true;
        try {
          const res = await axios.post(`${BASE_URL}/auth/refresh`, { refresh_token: refreshToken });
          const newToken = res.data.data.access_token;
          localStorage.setItem('access_token', newToken);
          if (error.config) {
            error.config.headers = error.config.headers || {};
            error.config.headers.Authorization = `Bearer ${newToken}`;
            return api(error.config);
          }
        } catch {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      } else {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
      }
    }

    // 423 — account/compliance lock; broadcast a DOM event for global UI handling
    if (error.response.status === 423) {
      window.dispatchEvent(new CustomEvent('account-locked', { detail: error.response.data }));
    }

    return Promise.reject(error);
  }
);

export default api;

// ─── Typed gap helpers ────────────────────────────────────────────────────────

/**
 * Fetch gaps scoped to a specific OE and iteration.
 * Sends both `oe_code` and `iteration_id` as query params so the backend can
 * filter at the DB level. The returned list is also filtered client-side on
 * `oe_code` as a safety net for backends that ignore unknown query params.
 */
export const getGapsByOe = (
  oeCode: string,
  iterationId: string,
): Promise<any[]> =>
  api
    .get('/gaps', { params: { oe_code: oeCode, iteration_id: iterationId } })
    .then((res) => {
      const all: any[] = res.data?.data ?? [];
      return all.filter((g) => !g.oe_code || g.oe_code === oeCode);
    });